import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { TackLogo } from "@/components/TackLogo";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";
import { ensureMerchantFn } from "@/lib/merchant-bootstrap.functions";
import { sendReferralBonusEmailFn } from "@/lib/email/email-senders.functions";
import { withAuth } from "@/lib/server-fn-auth";
import { toast } from "sonner";

export const Route = createFileRoute("/auth/callback")({
  component: AuthCallback,
});

function slugify(str: string) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function AuthCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function waitForSession() {
      const { data } = await supabase.auth.getSession();
      if (data.session) return data.session;

      return new Promise<import("@supabase/supabase-js").Session | null>((resolve) => {
        const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
          if (event === "SIGNED_IN" && session) {
            clearTimeout(timer);
            sub.subscription.unsubscribe();
            resolve(session);
          }
        });
        const timer = setTimeout(() => {
          sub.subscription.unsubscribe();
          resolve(null);
        }, 5000);
      });
    }

    (async () => {
      try {
        const session = await waitForSession();
        if (cancelled) return;
        if (!session) {
          setError("expired");
          return;
        }

        const meta = (session.user.user_metadata ?? {}) as Record<string, unknown>;
        const businessName =
          typeof meta.business_name === "string" ? (meta.business_name as string) : undefined;
        const referralCode =
          typeof meta.referral_code === "string" ? (meta.referral_code as string) : undefined;
        const countryCode =
          typeof meta.country_code === "string" ? (meta.country_code as string) : undefined;

        // Create merchant + owner-membership. Idempotent.
        let ensureRes = await withAuth(ensureMerchantFn)({
          data: { businessName, countryCode },
        });
        if (!ensureRes.ok) {
          await new Promise((r) => setTimeout(r, 300));
          ensureRes = await withAuth(ensureMerchantFn)({
            data: { businessName, countryCode },
          });
        }
        if (!ensureRes.ok) {
          console.error("[auth/callback] ensureMerchantFn failed:", ensureRes.error);
          toast.error(t("auth.setupFailed"));
          navigate({ to: "/onboarding", search: { rerun: undefined } });
          return;
        }

        // Best-effort slug rename to match the business name.
        if (businessName) {
          const slug = slugify(businessName);
          if (slug && slug !== "biz") {
            void supabase
              .from("merchants")
              .update({ slug })
              .eq("id", ensureRes.merchantId)
              .then(({ error }) => {
                if (error) console.warn("[auth/callback] slug rename skipped:", error.message);
              });
          }
        }

        // Apply referral code if it travelled through user_metadata.
        if (referralCode) {
          const { data: redeemRes, error: redeemErr } = await supabase.rpc(
            "redeem_referral_code",
            { _code: referralCode },
          );
          if (redeemErr) {
            console.error("[auth/callback] referral redeem error:", redeemErr);
          } else if (redeemRes && typeof redeemRes === "object" && "success" in redeemRes) {
            const r = redeemRes as {
              success: boolean;
              error?: string;
              inviter_business_name?: string;
              inviter_merchant_id?: string;
            };
            if (r.success) {
              toast.success(t("auth.referralAppliedToast"));
              if (r.inviter_merchant_id) {
                withAuth(sendReferralBonusEmailFn)({
                  data: {
                    inviterMerchantId: r.inviter_merchant_id,
                    inviteeBusinessName: businessName ?? "",
                  },
                }).catch((err: unknown) =>
                  console.error("Inviter notification failed:", err),
                );
              }
            } else if (r.error && r.error !== "already_referred") {
              console.warn("[auth/callback] referral not applied:", r.error);
            }
          }
        }

        if (!cancelled) navigate({ to: "/onboarding", search: { rerun: undefined } });
      } catch (err) {
        console.error("[auth/callback] failed:", err);
        if (!cancelled) setError("expired");
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <TackLogo size="lg" />
          </div>
          <Card>
            {error ? (
              <>
                <CardHeader className="text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                    <AlertCircle className="h-6 w-6 text-destructive" />
                  </div>
                  <CardTitle className="font-display">{t("auth.callbackExpiredTitle")}</CardTitle>
                  <CardDescription>{t("auth.callbackExpiredBody")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild className="w-full">
                    <Link to="/login">{t("auth.backToSignIn")}</Link>
                  </Button>
                </CardContent>
              </>
            ) : (
              <CardHeader className="text-center py-12">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                <CardTitle className="font-display mt-4 text-lg">
                  {t("auth.callbackVerifying")}
                </CardTitle>
              </CardHeader>
            )}
          </Card>
        </div>
      </div>
      <SiteFooter variant="legal" />
    </div>
  );
}
